import { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Loader2, Mail, Lock, User, Eye, EyeOff,
  GraduationCap, BookOpen, ArrowRight, CheckCircle, Star
} from "lucide-react";
import { toast } from "sonner";

const VIDEO_URL = "https://cdn.pixabay.com/video/2015/09/27/846-140823862_large.mp4";
const FALLBACK_IMG = "https://customer-assets.emergentagent.com/job_ef6b7e4c-45c9-49e1-9f50-8ce8e2fcd689/artifacts/3wtpyw4l_Fallback%20image.jpg";

export default function HeroLandingPage() {
  const navigate = useNavigate();
  const { register, initiateGoogleLogin, initiateMicrosoftLogin } = useAuth();
  const videoRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [microsoftLoading, setMicrosoftLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", password: "", confirmPassword: "", user_type: "student",
  });

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) { toast.error("Passwords do not match"); return; }
    if (formData.password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setLoading(true);
    try {
      await register({
        name: formData.name, email: formData.email, phone: formData.phone,
        password: formData.password, user_type: formData.user_type,
      });
      toast.success("Account created successfully!");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally { setLoading(false); }
  };

  const handleGoogleLogin = () => { setGoogleLoading(true); initiateGoogleLogin(); };
  const handleMicrosoftLogin = async () => {
    setMicrosoftLoading(true);
    try { await initiateMicrosoftLogin(); }
    catch { toast.error("Failed to initiate Microsoft login"); setMicrosoftLoading(false); }
  };

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="hero-landing-page">
      {/* Video Background */}
      {!videoError ? (
        <video
          ref={videoRef}
          autoPlay muted loop playsInline
          onError={() => setVideoError(true)}
          className="absolute inset-0 w-full h-full object-cover"
          poster={FALLBACK_IMG}
          data-testid="hero-video"
        >
          <source src={VIDEO_URL} type="video/mp4" />
        </video>
      ) : (
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url('${FALLBACK_IMG}')` }}
          data-testid="hero-fallback-image"
        />
      )}

      {/* Dark overlay (45% opacity) */}
      <div className="absolute inset-0 bg-black/[0.45]" />

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col lg:flex-row">
        {/* Left: Hero Text */}
        <div className="flex-1 flex flex-col justify-center px-6 sm:px-12 xl:px-20 py-12 lg:py-0 lg:max-w-[55%]">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            {/* Logo */}
            <Link to="/" className="inline-flex items-center gap-3 mb-8 lg:mb-12">
              <div className="w-12 h-12 rounded-2xl overflow-hidden shadow-lg bg-white/10 backdrop-blur-sm">
                <img src="/logo.png" alt="GMP" className="w-full h-full object-contain" />
              </div>
              <span className="text-xl sm:text-2xl font-bold text-white tracking-tight">Great Messiah Publication</span>
            </Link>

            {/* Headline */}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-tight mb-5" data-testid="hero-headline">
              Empowering Students.{" "}
              <span className="text-[#e0482d]">Guiding Teachers.</span>{" "}
              Inspiring Success.
            </h1>

            {/* Sub-headline */}
            <p className="text-base sm:text-lg text-slate-200/90 leading-relaxed mb-8 max-w-xl" data-testid="hero-subheadline">
              Your trusted hub for exam preparation, study resources, and academic growth.
            </p>

            {/* Benefits */}
            <div className="hidden sm:flex items-center gap-5 mb-8">
              {[
                { icon: BookOpen, text: "GES-aligned mock exams" },
                { icon: CheckCircle, text: "Trusted by 1000+ schools" },
                { icon: GraduationCap, text: "Expert-crafted content" },
              ].map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-2 text-sm text-white/80">
                  <Icon className="w-4 h-4 text-[#e0482d]" />
                  <span>{text}</span>
                </div>
              ))}
            </div>

            {/* CTA + Social Proof (mobile/tablet: visible, desktop: also visible below form) */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6 lg:mb-0">
              <Button
                onClick={() => document.getElementById("hero-form")?.scrollIntoView({ behavior: "smooth" })}
                className="bg-[#e0482d] hover:bg-[#c93d25] text-white font-semibold rounded-xl h-12 px-8 text-base lg:hidden"
                data-testid="hero-cta-mobile"
              >
                Get Started <ArrowRight className="w-5 h-5 ml-2" />
              </Button>

              <div className="flex items-center gap-4">
                <div className="flex -space-x-2">
                  {["KA", "AM", "IO", "NS"].map((init, i) => (
                    <div key={i} className="w-8 h-8 rounded-full border-2 border-white/20 bg-gradient-to-br from-slate-400 to-slate-600 flex items-center justify-center text-[10px] text-white font-bold">
                      {init}
                    </div>
                  ))}
                </div>
                <div>
                  <div className="flex items-center gap-0.5 mb-0.5">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-3 h-3 fill-amber-400 text-amber-400" />)}
                  </div>
                  <p className="text-slate-300/70 text-xs">50,000+ students trust us</p>
                </div>
              </div>
            </div>

            {/* Preview link */}
            <div className="hidden lg:flex items-center gap-4 mt-6">
              <Link to="/sample-preview" className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors" data-testid="hero-sample-preview-link">
                <GraduationCap className="w-4 h-4" /> Preview sample exam questions
              </Link>
              <span className="text-white/20">|</span>
              <Link to="/mock-exams" className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors" data-testid="hero-browse-link">
                <BookOpen className="w-4 h-4" /> Browse catalog
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Right: Sign-Up Form */}
        <div className="flex-1 flex items-center justify-center p-4 sm:p-8 lg:max-w-[45%]" id="hero-form">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="w-full max-w-md"
          >
            <div className="bg-white rounded-3xl shadow-2xl p-6 sm:p-8" data-testid="hero-signup-form">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-1">Get Started</h2>
              <p className="text-slate-500 text-sm mb-5">Create your account to begin</p>

              {/* OAuth */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <Button
                  type="button" variant="outline"
                  className="h-10 rounded-xl border-slate-200 hover:bg-slate-50 text-sm font-medium"
                  onClick={handleGoogleLogin}
                  disabled={googleLoading || microsoftLoading}
                  data-testid="hero-google-signup-btn"
                >
                  {googleLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                    <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                  )}
                  Google
                </Button>
                <Button
                  type="button" variant="outline"
                  className="h-10 rounded-xl border-slate-200 hover:bg-slate-50 text-sm font-medium"
                  onClick={handleMicrosoftLogin}
                  disabled={microsoftLoading || googleLoading}
                  data-testid="hero-microsoft-signup-btn"
                >
                  {microsoftLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                    <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                      <path fill="#F25022" d="M1 1h10v10H1z"/><path fill="#00A4EF" d="M1 13h10v10H1z"/>
                      <path fill="#7FBA00" d="M13 1h10v10H13z"/><path fill="#FFB900" d="M13 13h10v10H13z"/>
                    </svg>
                  )}
                  Microsoft
                </Button>
              </div>

              <div className="relative mb-4">
                <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-slate-200" /></div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-3 text-slate-400">Or sign up with email</span>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-3">
                <div>
                  <Label htmlFor="hero-name" className="text-xs font-semibold text-slate-600 mb-1 block">Full Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input id="hero-name" name="name" value={formData.name} onChange={handleChange}
                      placeholder="Enter your full name" className="pl-9 h-10 rounded-xl text-sm" required data-testid="hero-name" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="hero-email" className="text-xs font-semibold text-slate-600 mb-1 block">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input id="hero-email" name="email" type="email" value={formData.email} onChange={handleChange}
                      placeholder="your@email.com" className="pl-9 h-10 rounded-xl text-sm" required data-testid="hero-email" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="hero-user-type" className="text-xs font-semibold text-slate-600 mb-1 block">I am a</Label>
                  <Select value={formData.user_type} onValueChange={(v) => setFormData(p => ({ ...p, user_type: v }))}>
                    <SelectTrigger className="h-10 rounded-xl text-sm" data-testid="hero-user-type">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="student">Student</SelectItem>
                      <SelectItem value="teacher">Teacher</SelectItem>
                      <SelectItem value="parent">Parent</SelectItem>
                      <SelectItem value="school_admin">School Administrator</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="hero-pw" className="text-xs font-semibold text-slate-600 mb-1 block">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input id="hero-pw" name="password" type={showPassword ? "text" : "password"}
                        value={formData.password} onChange={handleChange}
                        placeholder="Min 8 chars" className="pl-9 pr-9 h-10 rounded-xl text-sm" required minLength={8} data-testid="hero-password" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" tabIndex={-1}>
                        {showPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="hero-cpw" className="text-xs font-semibold text-slate-600 mb-1 block">Confirm</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input id="hero-cpw" name="confirmPassword" type={showConfirm ? "text" : "password"}
                        value={formData.confirmPassword} onChange={handleChange}
                        placeholder="Confirm" className="pl-9 pr-9 h-10 rounded-xl text-sm" required data-testid="hero-confirm-password" />
                      <button type="button" onClick={() => setShowConfirm(!showConfirm)}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" tabIndex={-1}>
                        {showConfirm ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </div>
                </div>

                <Button type="submit" className="w-full h-11 rounded-xl bg-[#e0482d] hover:bg-[#c93d25] text-white font-semibold text-sm"
                  disabled={loading} data-testid="hero-submit">
                  {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating account...</>
                    : <>Get Started <ArrowRight className="w-4 h-4 ml-2" /></>}
                </Button>
              </form>

              <p className="text-center text-xs text-slate-500 mt-4">
                Already have an account?{" "}
                <Link to="/login" className="text-[#e0482d] font-semibold hover:underline" data-testid="hero-login-link">Sign in</Link>
              </p>

              {/* Mobile links */}
              <div className="flex items-center justify-center gap-3 mt-3 lg:hidden">
                <Link to="/sample-preview" className="text-xs text-[#e0482d]/70 hover:text-[#e0482d] transition-colors font-medium" data-testid="hero-sample-preview-link-mobile">
                  Preview exams
                </Link>
                <span className="text-slate-300">|</span>
                <Link to="/mock-exams" className="text-xs text-slate-400 hover:text-slate-600 transition-colors">
                  Browse catalog
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
